import { useEffect, useState, useMemo } from "react";
import {
  query,
  where,
  orderBy,
  limit,
  getCountFromServer,
} from "firebase/firestore";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { getOnixChunkReportingCollection } from "integrations/firebase/collections/onixChunkReporting";
import { logError } from "shared/services/ErrorReporting";
import { usePagination } from "./usePagination";

export const useOnixChunks = ({
  reportId,
  enabled = true,
}: {
  reportId: string;
  enabled?: boolean;
}) => {
  const { page, pageSize } = usePagination({
    defaultPage: 1,
    defaultPageSize: 10,
    prefix: "report",
  });

  const [totalCount, setTotalCount] = useState(0);

  // Base query for the collection
  const baseQuery = useMemo(
    () =>
      query(
        getOnixChunkReportingCollection(),
        where("reportId", "==", reportId),
        orderBy("status", "asc"), // 100=failed, 200=warning, 300=success
        orderBy("createdAt", "desc"), // Newest first within same status
      ),
    [reportId],
  );

  // Calculate slice boundaries
  const from = (page - 1) * pageSize;
  const to = from + pageSize;

  // Fetch all records up to current page
  const paginatedQuery = useMemo(
    () => (enabled ? query(baseQuery, limit(to)) : null),
    [enabled, baseQuery, to],
  );

  const [snapshot = [], loading, error] = useCollectionData(paginatedQuery);

  // Extract data from snapshot and slice to current page
  const chunks = useMemo(() => snapshot.slice(from, to), [snapshot, from, to]);

  // Fetch total count
  useEffect(() => {
    if (!enabled) return;

    const fetchCount = async () => {
      try {
        const countSnapshot = await getCountFromServer(baseQuery);
        setTotalCount(countSnapshot.data().count);
      } catch (err) {
        setTotalCount(0);
        logError(err);
      }
    };

    fetchCount();
  }, [enabled, baseQuery]);

  return {
    chunks,
    loading,
    itemsCount: totalCount,
    error,
  };
};
